//@flow
import {
    teamMaster as teamApi,
    bulkTeamFetch as bulkTeamFetchApi,
    teamStatusUpdate as teamStatusUpdateApi,
    bulkDisableTeam as bulkDisableTeamApi,
    validateEntityIds as validateEntityIdsApi
} from 'common/middleware/api';
import { showToast } from 'common/redux/actions/toast';
import { ToastTypes } from 'common/components/core/toast/Toast';
import PaginatedEntityActions from 'common/redux/actions/PaginatedEntityActions';
import I18nHelper from 'common/helpers/I18nHelper';

import { MasterTeam, TeamStatusEnum } from 'common/models/internal/MasterTeam';
import type { Dispatch } from 'redux';
import { ENTITY_NAME_ID } from 'common/constants/entity/teamMaster';
import { ApiError } from 'common/modules/api';
import { injectError } from 'common/redux/actions/error';
import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

type IncludeTypeEnumType = $Values<typeof IncludeTypeEnums>;
type Params = {
    clientId: string,
    teamId?: string,
    include?: IncludeTypeEnumType | Array<IncludeTypeEnumType>
};

class TeamActions extends PaginatedEntityActions<MasterTeam, Params> {
    entityNameId = ENTITY_NAME_ID;
    superAddEntity = this.addEntity;

    addEntity = (
        params: { clientId: string, teamId: string },
        entity: MasterTeam,
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (team: MasterTeam) => {
                        const toast = {
                            message: nsTranslate('addEntitySuccessMessage', {
                                entityNameId: 'team'
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(team);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError: true
                };
                dispatch(this.superAddEntity(params, entity, options));
            });
        };
    };

    superPatchEntity = this.patchEntity;

    patchEntity = (
        params: { clientId: string, teamId: string },
        oldEntity: MasterTeam,
        newEntity: MasterTeam,
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const [oldEntitySanitized, newEntitySanitized] = [
                    oldEntity,
                    newEntity
                ].map(entity => entity.sanitize());
                const options = {
                    onSuccess: (team: MasterTeam) => {
                        const toast = {
                            message: nsTranslate('savedChangesMessage'),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(team);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    },
                    dontInjectError: true
                };
                dispatch(
                    this.superPatchEntity(
                        params,
                        oldEntitySanitized,
                        newEntitySanitized,
                        options
                    )
                );
            });
        };
    };

    entityStatusUpdate = this.entityStatusUpdate;

    disableEntity = (
        params: { clientId: string, teamId: string },
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (team: MasterTeam) => {
                        const toast = {
                            message: nsTranslate(
                                'disableEntitySuccessMessage',
                                {
                                    entityNameId: 'team'
                                }
                            ),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(team);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    }
                };
                const body = {
                    status: TeamStatusEnum.Inactive
                };
                dispatch(this.entityStatusUpdate(params, options, body));
            });
        };
    };

    enableEntity = (
        params: { clientId: string, teamId: string },
        _options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: (team: MasterTeam) => {
                        const toast = {
                            message: nsTranslate('enableEntitySuccessMessage', {
                                entityNameId: 'team'
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve(team);
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    }
                };
                const body = {
                    status: TeamStatusEnum.Active
                };
                dispatch(this.entityStatusUpdate(params, options, body));
            });
        };
    };

    fetchEntity = (params: Params) => {
        return (dispatch: Dispatch<$FixMe>) => {
            dispatch(this.fetchTeamStart(params));
            return teamApi
                .fetch(params)
                .then(team => {
                    const entity = MasterTeam.fromJS(team);
                    dispatch(this.fetchTeamSuccess({ params, entity }));
                    return entity;
                })
                .catch(error => {
                    if (error instanceof ApiError) {
                        dispatch(injectError(error));
                    } else {
                        throw error;
                    }
                });
        };
    };

    fetchTeamStart = (params: Params) => ({
        type: this.types.FETCH_ENTITY_START,
        params
    });

    fetchTeamSuccess = ({
        params,
        entity
    }: {
        params: Params,
        entity: MasterTeam
    }) => ({
        params,
        entity,
        type: this.types.FETCH_ENTITY_SUCCESS
    });
}

export default new TeamActions(
    'Team',
    teamApi,
    bulkTeamFetchApi,
    null,
    bulkDisableTeamApi,
    validateEntityIdsApi,
    null,
    teamStatusUpdateApi
);
